import { Link } from "react-router-dom";
import { ArrowRight, User, Building2, GraduationCap, Trophy, Users, UploadCloud, Quote } from "lucide-react";
import Marquee from "@/components/Marquee";
import Hero from "@/components/home/Hero";
import Spotlights from "@/components/home/Spotlights";
import BentoServices from "@/components/home/BentoServices";
import { Reveal } from "@/components/Motion";
import { GALLERY, AUDIENCES, BUSINESS } from "@/lib/site";
import usePageTitle from "@/lib/usePageTitle";

const AUDIENCE_ICONS = { user: User, building: Building2, school: GraduationCap, trophy: Trophy, users: Users };
const FEATURED = ["g11", "g8", "g3", "g9", "g2"];

export default function Home() {
  usePageTitle("Skies The Limit Creations LLC | 3D Printing, Laser Engraving, Embroidery & DTF");
  const featured = FEATURED.map((id) => GALLERY.find((g) => g.id === id)).filter(Boolean);

  return (
    <div className="grain">
      <Hero />
      <Marquee />
      <Spotlights />
      <BentoServices />

      {/* Selected real work */}
      <section className="border-y border-white/10 bg-panel/40 py-20 lg:py-28" data-testid="home-work-preview">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="eyebrow mb-4">Selected real work</p>
                <h2 className="max-w-xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  Straight from our hands to yours.
                </h2>
              </div>
              <Link to="/work" data-testid="home-work-view-all-button" className="btn-ghost !px-6 !py-3 text-xs">
                View Our Work <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 [scrollbar-width:thin]">
              {featured.map((g) => (
                <figure key={g.id} className="w-[260px] shrink-0 snap-start sm:w-[300px]" data-testid={`home-work-card-${g.id}`}>
                  <div className="overflow-hidden rounded-2xl border border-white/10">
                    <img
                      src={g.src}
                      alt={g.alt}
                      loading="lazy"
                      className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>
                  <figcaption className="mt-3 flex items-center justify-between gap-3 text-xs text-mist">
                    <span>{g.caption}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-gold">{g.category}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Who we serve */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28" data-testid="home-audiences-section">
        <Reveal>
          <p className="eyebrow mb-4">Who we work with</p>
          <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
            One shop, five kinds of customers.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-5">
          {AUDIENCES.map((a, i) => {
            const Icon = AUDIENCE_ICONS[a.icon];
            return (
              <Reveal key={a.label} delay={i * 0.08}>
                <div className="card-panel h-full p-6 transition-colors duration-300 hover:border-gold/40">
                  <Icon className="h-6 w-6 text-ember" aria-hidden />
                  <h3 className="mt-4 font-display text-base font-bold text-white">{a.label}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-mist">{a.line}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Kind words — placeholders until real testimonials arrive */}
      <section className="border-t border-white/10 bg-panel/40 py-20 lg:py-28" data-testid="home-testimonials-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow mb-4">Kind words</p>
            <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Customers who sent us soaring.
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-mist">
              Real quotes from real customers belong here — nothing is made up. Send us your favorite testimonials
              and we&apos;ll fill these frames.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="placeholder-frame min-h-[220px] p-8" data-testid={`home-testimonial-placeholder-${i + 1}`}>
                  <Quote className="h-7 w-7 text-gold/60" aria-hidden />
                  <p className="font-display text-base font-bold text-white">[ Add a customer testimonial ]</p>
                  <p className="max-w-[240px] text-xs leading-relaxed text-mist">
                    {i === 0
                      ? "A favorite quote from a customer, team, or school — in their words."
                      : "Name or team goes underneath, e.g. “Jane D. — team mom”."}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Quote band */}
      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-32" data-testid="home-quote-band">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-panel px-6 py-16 text-center sm:px-12 lg:py-20">
            <div aria-hidden className="pointer-events-none absolute -top-24 left-1/2 h-[380px] w-[380px] -translate-x-1/2 rounded-full bg-ember/20 blur-[110px]" />
            <p className="eyebrow relative">No bots, no instant estimates</p>
            <h2 className="relative mx-auto mt-4 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
              Ready to make something?
            </h2>
            <p className="relative mx-auto mt-5 max-w-xl text-sm leading-relaxed text-mist sm:text-base">
              Tell us your idea and a real person will reply with a personal quote. Submissions land directly in our
              inbox at{" "}
              <a href={`mailto:${BUSINESS.email}`} className="text-gold hover:underline" data-testid="home-quote-band-email-link">
                {BUSINESS.email}
              </a>
            </p>
            <div className="relative mt-9 flex justify-center">
              <Link to="/quote" data-testid="home-quote-band-cta-button" className="btn-primary">
                Request a Quote <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
            <UploadCloud className="relative mx-auto mt-10 h-5 w-5 text-mist/50" aria-hidden />
            <p className="relative mt-2 text-xs text-mist/70">Artwork or reference images welcome with every request</p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
